import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getColorCatalog, getSubscriptionStatus } from '../api/colors';
import { createColorPayment, createSubscriptionPayment } from '../api/payment';
import { ColorInfo, SubscriptionStatus } from '../types/color';

interface ColorsState {
  catalog: ColorInfo[];
  subscription: SubscriptionStatus;
  loading: boolean;
  error: string | null;
}

const initialState: ColorsState = {
  catalog: [],
  subscription: { active: false, expires_at: null },
  loading: false,
  error: null,
};

/** Fetch current color catalog + subscription status from the server. */
export const fetchColorCatalog = createAsyncThunk('colors/fetchCatalog', async () => {
  const [catRes, subRes] = await Promise.all([
    getColorCatalog(),
    getSubscriptionStatus(),
  ]);
  return { catalog: catRes.data, subscription: subRes.data };
});

/**
 * Initiate a Robokassa payment for a single color.
 * Returns the payment URL — the caller is responsible for redirecting the browser.
 */
export const initiateColorPayment = createAsyncThunk(
  'colors/initiateColorPayment',
  async ({ color, returnPath }: { color: string; returnPath?: string }) => {
    const res = await createColorPayment(color, returnPath);
    return res.data.payment_url;
  },
);

/**
 * Initiate a Robokassa payment for a monthly subscription.
 * Returns the payment URL — the caller is responsible for redirecting the browser.
 */
export const initiateSubscriptionPayment = createAsyncThunk(
  'colors/initiateSubscriptionPayment',
  async () => {
    const res = await createSubscriptionPayment();
    return res.data.payment_url;
  },
);

const colorsSlice = createSlice({
  name: 'colors',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchColorCatalog.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchColorCatalog.fulfilled, (state, action) => {
        state.loading = false;
        state.catalog = action.payload.catalog;
        state.subscription = action.payload.subscription;
      })
      .addCase(fetchColorCatalog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to load colors';
      });
  },
});

export default colorsSlice.reducer;
